import { css } from '@emotion/react';
import React, { Fragment, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import t from 'react-translate';
import { Button } from 'react-bootstrap';
import ClickableContainer from 'components/clickable-container';
import NvFlyoutModal, { ModalType } from 'shared/components/nv-flyout-modal';
import NvCheckbox from 'shared/components/inputs/nv-checkbox';
import NvSelect, { SelectOption } from 'shared/components/inputs/nv-select';
import NvTextInput from 'shared/components/inputs/nv-text-input';
import { RootState } from 'redux/schemas';
import { CourseExercise } from 'redux/schemas/models/exercise';
import { AssignmentAliases } from 'redux/schemas/models/course';
import { getCourseAliases } from 'redux/selectors/course';
import { gray2, gray3, gray6, gray7, info, white } from 'styles/global_defaults/colors';
import { textSmallFontSize, textLargeBodyFontSize, semiBoldFontWeight } from 'styles/global_defaults/fonts';
import { halfSpacing, largeSpacing, quarterSpacing, standardSpacing, threeQuartersSpacing, tripleSpacing } from 'styles/global_defaults/scaffolding';
import { screenXsMax } from 'styles/global_defaults/media-queries';

export type LearnerProgressFilters = {
  inactiveForEnabled: boolean;
  inactiveFor: number | null;
  assignmentsEnabled: boolean;
  assignmentId: number | null;
  assignmentName: string | null;
  minPointsEnabled: boolean;
  minPoints: number | null;
  viewName: string | null;
};

export const DEFAULT_FILTERS: LearnerProgressFilters = {
  inactiveForEnabled: false,
  viewName: '',
  inactiveFor: null,
  assignmentsEnabled: false,
  assignmentId: null,
  assignmentName: null,
  minPointsEnabled: false,
  minPoints: null,
};

type FiltersFlyoutModalProps = {
  showModal: boolean;
  onClose: () => void;
  onApply: (filters: LearnerProgressFilters) => void;
  initialFilters?: LearnerProgressFilters;
  isEditMode?: boolean;
  gamificationEnabled?: boolean;
  totalPoints?: number | null;
};

type FiltersFlyoutFormProps = {
  onClose: () => void;
  onApply: (filters: LearnerProgressFilters) => void;
  initialFilters: LearnerProgressFilters;
  activities: { id: number; title: string }[];
  assignmentAliases: AssignmentAliases;
  isEditMode?: boolean;
  gamificationEnabled: boolean;
  totalPoints: number | null;
};

const styles = css`
  &:focus {
    outline: none !important;
  }

  .header {
    height: ${tripleSpacing}px;
    position: sticky;
    top: 0;
    background: ${white};
    z-index: 10;
    margin: 0;
  }

  .filters-actions {
    position: sticky;
    top: ${tripleSpacing}px;
    background: ${white};
    z-index: 10;
  }

  .filter-row {
    background-color: ${gray7};

    &.disabled {
      background-color: ${gray6};
    }

    &.active {
      background-color: ${info};
    }
    border-radius: ${halfSpacing}px;
    padding: ${threeQuartersSpacing}px ${standardSpacing}px;
    margin-bottom: ${threeQuartersSpacing}px;

    .filter-checkbox-row {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: ${halfSpacing}px;

      .nv-checkbox-label {
        font-size: ${textLargeBodyFontSize}px;
      }

      .filter-disabled-text {
        margin-left: auto;
        color: ${gray3};
        font-size: ${textSmallFontSize}px;
        font-weight: ${semiBoldFontWeight};
      }
    }

    .points-total-hint {
      margin-top: ${quarterSpacing}px;
      color: ${gray2};
      font-weight: ${semiBoldFontWeight};
      font-size: ${textSmallFontSize}px;
    }

    .inactive-days-input {
      width: 70px;

      .bs4-input-group {
        height: 30px;
      }

      .bs4-form-control {
        text-align: center;
        height: 30px;
      }
    }

    .assignment-dropdown {
      flex: 1;
      min-width: 150px;

      @media (max-width: ${screenXsMax}px) {
        flex-basis: 100%;
      }
    }

    .assignment-dropdown-select {
      width: 100%;
      position: relative;

      .text-medium {
        height: 45px;
        background-color: ${white};
      }
      div {
        width: 100%;
      }
      .select-menu {
        max-height: 200px;
        overflow-y: auto;
        width: 100% !important;
        transform: none !important;
        top: 100% !important;
        left: 0 !important;

        > div {
          height: auto;
          min-height: ${largeSpacing}px;
          padding: ${halfSpacing}px ${threeQuartersSpacing}px;
        }
      }
    }
  }
`;

const FiltersFlyoutForm = ({
  onClose,
  onApply,
  initialFilters,
  activities,
  assignmentAliases,
  isEditMode = false,
  gamificationEnabled,
  totalPoints,
}: FiltersFlyoutFormProps) => {
  const hasAssignments = activities.length > 0;
  const containerRef = useRef<HTMLDivElement>(null);
  const { watch, setValue, handleSubmit, reset } = useForm<LearnerProgressFilters>({
    mode: 'onChange',
    defaultValues: initialFilters,
  });

  useEffect(() => {
    containerRef.current?.focus();
  }, []);

  const formValues = watch();

  const hasFormChanged = () => {
    const { inactiveForEnabled, inactiveFor, assignmentsEnabled, assignmentId, minPointsEnabled, minPoints, viewName } = formValues;
    return (
      inactiveForEnabled !== initialFilters.inactiveForEnabled
      || inactiveFor !== initialFilters.inactiveFor
      || assignmentsEnabled !== initialFilters.assignmentsEnabled
      || assignmentId !== initialFilters.assignmentId
      || minPointsEnabled !== initialFilters.minPointsEnabled
      || minPoints !== initialFilters.minPoints
      || (viewName ?? '').trim() !== (initialFilters.viewName ?? '').trim()
    );
  };

  const isApplyDisabled = () => {
    const { inactiveForEnabled, inactiveFor, assignmentsEnabled, assignmentId, minPointsEnabled, minPoints, viewName } = formValues;

    if (!inactiveForEnabled && !assignmentsEnabled && !minPointsEnabled) return true;
    if (inactiveForEnabled && (!inactiveFor || inactiveFor < 1)) return true;
    if (assignmentsEnabled && !assignmentId) return true;
    if (minPointsEnabled && (minPoints === null || minPoints < 1)) return true;
    if (minPointsEnabled && minPoints !== null && totalPoints != null && minPoints > totalPoints) return true;
    if (isEditMode && !viewName?.trim()) return true;
    if (isEditMode && !hasFormChanged()) return true;

    return false;
  };

  const resetFilters = () => {
    reset(DEFAULT_FILTERS);
  };

  const onSubmit = (data: LearnerProgressFilters) => {
    onApply(data);
    onClose();
  };

  const selectOptions: SelectOption[] = activities.map((activity) => ({
    value: activity.id,
    label: activity.title,
    dataQa: `LEARNER_PROGRESS_FILTERS_OPTION_${activity.id}`,
  }));

  return (
    <div css={styles} ref={containerRef} tabIndex={-1} role='region' aria-label={t.LEARNER_PROGRESS.FILTERS.TITLE()}>
      <h2 className='header d-flex justify-content-center align-items-center text-large-regular font-weight-bolder'>
        {t.LEARNER_PROGRESS.FILTERS.TITLE()}
      </h2>
      <div className={`filters-actions d-flex align-items-center pt-2 pb-4 px-4 ${isEditMode ? 'justify-content-end' : 'justify-content-between'}`}>
        {!isEditMode && (
          <ClickableContainer
            onClick={resetFilters}
            className='text-primary'
            data-qa='LEARNER_PROGRESS_FILTERS_RESET_BUTTON'
          >
            {t.LEARNER_PROGRESS.FILTERS.RESET_FILTERS()}
          </ClickableContainer>
        )}
        <Button
          onClick={handleSubmit(onSubmit)}
          disabled={isApplyDisabled()}
          data-qa='LEARNER_PROGRESS_FILTERS_APPLY_BUTTON'
        >
          {isEditMode ? t.FORM.UPDATE() : t.LEARNER_PROGRESS.FILTERS.APPLY_FILTERS()}
        </Button>
      </div>
      {isEditMode && (
        <div className='px-4 pb-4 text-regular'>
          <div className='pb-2 text-regular font-weight-bold'>
            {t.LEARNER_PROGRESS.FILTERS.NAME_OF_VIEW()}
          </div>
          <NvTextInput
            name='viewName'
            value={formValues.viewName ?? ''}
            ariaLabel={t.LEARNER_PROGRESS.FILTERS.NAME_OF_VIEW()}
            onChange={(e) => setValue('viewName', e.target.value)}
            data-qa='LEARNER_PROGRESS_FILTERS_VIEW_NAME_INPUT'
          />
        </div>
      )}
      <div className='px-4 pb-3 text-large-body'>
        {t.LEARNER_PROGRESS.FILTERS.DESCRIPTION()}
      </div>
      <div className='px-4'>
        <div className={`filter-row${formValues.inactiveForEnabled ? ' active' : ''}`}>
          <div className='filter-checkbox-row'>
            <NvCheckbox
              name='inactiveForEnabled'
              label={formValues.inactiveForEnabled
                ? t.LEARNER_PROGRESS.FILTERS.INACTIVE_FOR_MORE_THAN()
                : t.LEARNER_PROGRESS.FILTERS.INACTIVE_FOR_MORE_THAN_X_DAYS()}
              checked={formValues.inactiveForEnabled}
              onChange={(e) => setValue('inactiveForEnabled', e.target.checked)}
              dataQa='LEARNER_PROGRESS_FILTERS_INACTIVE_DAYS_CHECKBOX'
            />
            {formValues.inactiveForEnabled && (
              <Fragment>
                <NvTextInput
                  type='number'
                  min={1}
                  required
                  className='inactive-days-input'
                  value={formValues.inactiveFor ?? ''}
                  onChange={(e) => {
                    const val = e.target.value ? parseInt(e.target.value, 10) : null;
                    setValue('inactiveFor', val);
                  }}
                  ariaLabel={t.LEARNER_PROGRESS.FILTERS.DAYS()}
                  data-qa='LEARNER_PROGRESS_FILTERS_INACTIVE_DAYS_INPUT'
                />
                <span className='text-regular'>{t.LEARNER_PROGRESS.FILTERS.DAYS()}</span>
              </Fragment>
            )}
          </div>
        </div>
        <div className={`filter-row${formValues.assignmentsEnabled && hasAssignments ? ' active' : ''}${!hasAssignments ? ' disabled' : ''}`}>
          <div className='filter-checkbox-row'>
            <NvCheckbox
              name='assignmentsEnabled'
              label={formValues.assignmentsEnabled && hasAssignments
                ? t.LEARNER_PROGRESS.FILTERS.HAS_NOT_COMPLETED()
                : t.LEARNER_PROGRESS.FILTERS.HAS_NOT_COMPLETED_SPECIFIC()}
              checked={formValues.assignmentsEnabled && hasAssignments}
              disabled={!hasAssignments}
              onChange={(e) => setValue('assignmentsEnabled', e.target.checked)}
              dataQa='LEARNER_PROGRESS_FILTERS_NOT_COMPLETED_CHECKBOX'
            />
            {!hasAssignments && (
              <span className='filter-disabled-text'>
                {t.LEARNER_PROGRESS.FILTERS.NO_ASSIGNMENTS(assignmentAliases)}
              </span>
            )}
            {formValues.assignmentsEnabled && hasAssignments && (
              <div className='assignment-dropdown'>
                <NvSelect
                  options={selectOptions}
                  className='assignment-dropdown-select'
                  value={formValues.assignmentId}
                  placeholder={t.LEARNER_PROGRESS.FILTERS.PLEASE_SELECT()}
                  ariaLabel={t.LEARNER_PROGRESS.FILTERS.HAS_NOT_COMPLETED()}
                  onChange={(value) => {
                    const activity = activities.find((a) => a.id === value);
                    setValue('assignmentId', value);
                    setValue('assignmentName', activity?.title ?? null);
                  }}
                />
              </div>
            )}
          </div>
        </div>
        <div className={`filter-row${formValues.minPointsEnabled && gamificationEnabled ? ' active' : ''}${!gamificationEnabled ? ' disabled' : ''}`}>
          <div className='filter-checkbox-row'>
            <NvCheckbox
              name='minPointsEnabled'
              label={formValues.minPointsEnabled && gamificationEnabled
                ? t.LEARNER_PROGRESS.FILTERS.HAS_EARNED_FEWER_THAN()
                : t.LEARNER_PROGRESS.FILTERS.HAS_EARNED_FEWER_THAN_X_POINTS()}
              checked={formValues.minPointsEnabled && gamificationEnabled}
              disabled={!gamificationEnabled}
              onChange={(e) => setValue('minPointsEnabled', e.target.checked)}
              dataQa='LEARNER_PROGRESS_FILTERS_MIN_POINTS_CHECKBOX'
            />
            {gamificationEnabled && formValues.minPointsEnabled && (
              <Fragment>
                <NvTextInput
                  type='number'
                  min={1}
                  max={totalPoints ?? undefined}
                  required
                  className='inactive-days-input'
                  value={formValues.minPoints ?? ''}
                  onChange={(e) => {
                    const val = e.target.value ? parseInt(e.target.value, 10) : null;
                    setValue('minPoints', val);
                  }}
                  ariaLabel={t.LEARNER_PROGRESS.FILTERS.POINTS()}
                  data-qa='LEARNER_PROGRESS_FILTERS_MIN_POINTS_INPUT'
                />
                <span className='text-regular'>{t.LEARNER_PROGRESS.FILTERS.POINTS()}</span>
              </Fragment>
            )}
            {!gamificationEnabled && (
              <span className='filter-disabled-text' data-qa='LEARNER_PROGRESS_FILTERS_POINTS_DISABLED'>
                {t.LEARNER_PROGRESS.FILTERS.POINTS_NOT_ENABLED()}
              </span>
            )}
          </div>
          {gamificationEnabled && formValues.minPointsEnabled && totalPoints != null && (
            <div className='points-total-hint'>
              {t.LEARNER_PROGRESS.FILTERS.POINTS_TOTAL(String(totalPoints))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const FiltersFlyoutModal = ({
  showModal,
  onClose,
  onApply,
  initialFilters,
  isEditMode = false,
  gamificationEnabled = false,
  totalPoints = null,
}: FiltersFlyoutModalProps) => {
  const exercises: CourseExercise[] = useSelector((state: RootState) => state.app.lecturePage.exercises);
  const aliases = useSelector((state: RootState) => getCourseAliases(state));

  const activities = exercises.map((exercise) => ({
    id: exercise.id,
    title: exercise.title,
  }));

  const mobileStyles = css`
    .flyout-modal .flyout-content {
      min-width: min(600px, 100vw);
      width: min(600px, 100vw);
    }
  `;

  return (
    <Fragment>
      {showModal && (
        <div css={mobileStyles}>
          <NvFlyoutModal
            type={ModalType.RIGHT}
            header='Filters'
            width={600}
            onClose={onClose}
            closeDataQa='filters-flyout-close-button'
          >
            <FiltersFlyoutForm
              onClose={onClose}
              onApply={onApply}
              initialFilters={initialFilters ?? DEFAULT_FILTERS}
              activities={activities}
              assignmentAliases={aliases.assignmentAliases}
              isEditMode={isEditMode}
              gamificationEnabled={gamificationEnabled}
              totalPoints={totalPoints}
            />
          </NvFlyoutModal>
        </div>
      )}
    </Fragment>
  );
};

export default FiltersFlyoutModal;
